import React, { useCallback, useMemo } from 'react';
import { Typography, Tag, Image, Descriptions } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import CrudPage from '../../components/common/CrudPage';
import feedbackService from '../../services/survey/feedbackService';
import feedbackFeatureConfigService from '../../services/survey/feedbackFeatureConfigService';
import { FEEDBACK_STATUS, FEEDBACK_STATUS_OPTIONS } from '../../constants/survey';

const { Text, Paragraph } = Typography;

const renderStatusTag = (status) => {
  const matched = FEEDBACK_STATUS_OPTIONS.find((o) => o.value === Number(status));
  return matched ? <Tag color={matched.color}>{matched.label.toUpperCase()}</Tag> : <Tag>—</Tag>;
};

const renderFeature = (record) =>
  record.feature_id ? (
    <Tag color="blue">
      [#{record.feature_id}] {record.feature_name || `Tính năng #${record.feature_id}`}
    </Tag>
  ) : (
    <Text type="secondary">Không chọn</Text>
  );

const renderSender = (record) => (
  <div className="text-xs leading-5">
    {record.user_id ? <div>User: <Text strong>{record.user_id}</Text></div> : <Tag>Khách</Tag>}
    {record.customer_id && <div className="text-gray-400 break-all">{record.customer_id}</div>}
  </div>
);

// Góp ý người dùng gửi từ màn "Góp ý với Lịch Việt". Nội dung giữ nguyên như người dùng gửi,
// CMS chỉ cập nhật trạng thái xử lý và ghi chú nội bộ.
const FeedbackManagement = () => {
  const { data: rawFeatures } = useQuery({
    queryKey: ['feedbackFeatureConfigsForFilter'],
    queryFn: () => feedbackFeatureConfigService.getList({ page: 1, limit: 100 }),
  });

  const filterFields = useMemo(() => {
    const features = Array.isArray(rawFeatures?.data) ? rawFeatures.data : [];
    return [
      {
        label: 'Trạng thái xử lý',
        value: 'status',
        options: [
          { label: 'Tất cả trạng thái', value: '' },
          ...FEEDBACK_STATUS_OPTIONS.map((o) => ({ label: o.label, value: String(o.value) })),
        ],
      },
      {
        label: 'Tính năng',
        value: 'feature_id',
        options: [
          { label: 'Tất cả tính năng', value: '' },
          { label: 'Không chọn tính năng', value: 'none' },
          ...features.map((f) => ({ label: f.feature_name, value: String(f.feature_id) })),
        ],
      },
      {
        label: 'Nền tảng',
        value: 'platform',
        options: [
          { label: 'Tất cả nền tảng', value: '' },
          { label: 'iOS', value: 'ios' },
          { label: 'Android', value: 'android' },
        ],
      },
      { label: 'Từ ngày', value: 'from_date', type: 'date', submitFormat: 'YYYY-MM-DD' },
      { label: 'Đến ngày', value: 'to_date', type: 'date', submitFormat: 'YYYY-MM-DD' },
    ];
  }, [rawFeatures]);

  const formFields = useCallback(
    ({ record }) => [
      { type: 'divider', label: 'Nội dung góp ý' },
      {
        type: 'html',
        content: record ? (
          <div className="space-y-4">
            <Paragraph className="whitespace-pre-wrap bg-gray-50 rounded-xl p-4 !mb-0">{record.content}</Paragraph>
            {Array.isArray(record.images) && record.images.length > 0 && (
              <Image.PreviewGroup>
                <div className="flex gap-3 flex-wrap">
                  {record.images.map((url) => (
                    <Image key={url} src={url} width={120} height={120} className="object-cover rounded-lg" />
                  ))}
                </div>
              </Image.PreviewGroup>
            )}
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Mã góp ý">#{record.id}</Descriptions.Item>
              <Descriptions.Item label="Thời gian gửi">
                {record.created_at ? dayjs(record.created_at).format('DD/MM/YYYY HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Tính năng">{renderFeature(record)}</Descriptions.Item>
              <Descriptions.Item label="Người gửi">{renderSender(record)}</Descriptions.Item>
              <Descriptions.Item label="Thiết bị">
                {[record.platform, record.device_model, record.os_version].filter(Boolean).join(' · ') || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Phiên bản app">{record.app_version || '-'}</Descriptions.Item>
            </Descriptions>
          </div>
        ) : null,
      },
      { type: 'divider', label: 'Xử lý' },
      {
        name: 'status',
        label: 'Trạng thái xử lý',
        type: 'select',
        rules: [{ required: true, message: 'Vui lòng chọn trạng thái!' }],
        options: FEEDBACK_STATUS_OPTIONS.map(({ label, value }) => ({ label, value })),
        defaultValue: FEEDBACK_STATUS.NEW,
        md: 8,
      },
      {
        name: 'admin_note',
        label: 'Ghi chú nội bộ',
        type: 'textarea',
        rows: 3,
        placeholder: 'Ví dụ: đã chuyển đội phụ trách, đã phản hồi người dùng...',
      },
    ],
    []
  );

  const mapFormToData = (values) => ({
    status: Number(values.status),
    admin_note: values.admin_note?.trim() || null,
  });

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
        align: 'center',
      },
      {
        title: 'THỜI GIAN GỬI',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 150,
        render: (date) => (date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'),
      },
      {
        title: 'TÍNH NĂNG',
        dataIndex: 'feature_id',
        key: 'feature_id',
        width: 200,
        render: (_, record) => renderFeature(record),
      },
      {
        title: 'NỘI DUNG',
        dataIndex: 'content',
        key: 'content',
        render: (content) => <div className="line-clamp-2 whitespace-pre-wrap">{content}</div>,
      },
      {
        title: 'ẢNH',
        dataIndex: 'images',
        key: 'images',
        width: 90,
        align: 'center',
        render: (images) =>
          Array.isArray(images) && images.length > 0 ? <Tag color="purple">{images.length} ảnh</Tag> : '-',
      },
      {
        title: 'NGƯỜI GỬI',
        dataIndex: 'user_id',
        key: 'user_id',
        width: 200,
        render: (_, record) => renderSender(record),
      },
      {
        title: 'THIẾT BỊ',
        dataIndex: 'platform',
        key: 'platform',
        width: 130,
        render: (platform, record) => (
          <div className="text-xs">
            <div>{platform || '-'}</div>
            {record.app_version && <div className="text-gray-400">v{record.app_version}</div>}
          </div>
        ),
      },
      {
        title: 'TRẠNG THÁI',
        dataIndex: 'status',
        key: 'status',
        width: 130,
        align: 'center',
        render: renderStatusTag,
      },
    ],
    []
  );

  return (
    <CrudPage
      title="GÓP Ý NGƯỜI DÙNG"
      subTitle="Góp ý gửi từ màn Góp ý với Lịch Việt trên app"
      itemsName="Góp ý"
      queryKey="feedbackList"
      service={feedbackService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      mapFormToData={mapFormToData}
      searchPlaceholder="Tìm trong nội dung góp ý..."
      disableAdd
      disableCopy
      disableDelete
      formConfig={{ width: 900 }}
    />
  );
};

export default FeedbackManagement;
